import { NgFor, NgIf } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormArray,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import {
  NbAccordionModule,
  NbButtonModule,
  NbCardModule,
  NbCheckboxModule,
  NbDialogRef,
  NbDialogService,
  NbIconModule,
  NbInputModule,
  NbOptionModule,
  NbSelectModule,
  NbToastrService,
  NbTooltipModule
} from '@nebular/theme';
import { Actions, ofActionCompleted, Store } from '@ngxs/store';
import { Subject, takeUntil } from 'rxjs';
import { Survey } from 'src/app/models/survey';
import { ACTIONS } from 'src/app/shared/constants/general-constants';
import { NgxGenericModalComponent } from 'src/app/shared/ui/ngx-generic-modal/ngx-generic-modal.component';
import { SurveyQuestionsComponent } from './survey-questions/survey-questions.component';
import { PostSurveys, SaveSurveysComplete } from '../../redux/surveys.actions';
import { SurveysState } from '../../redux/surveys.state';

@Component({
  selector: 'app-edit-survey',
  templateUrl: './edit-survey.component.html',
  styleUrls: ['./edit-survey.component.css'],
  imports: [
    NbCardModule,
    NgIf,
    NbIconModule,
    NbButtonModule,
    FormsModule,
    ReactiveFormsModule,
    NbInputModule,
    NbSelectModule,
    NgFor,
    NbOptionModule,
    NbAccordionModule,
    NbTooltipModule,
    NbCheckboxModule,
    SurveyQuestionsComponent
  ],
  standalone: true
})
export class EditSurveyComponent implements OnInit, AfterViewInit, OnDestroy {
  private unsubscribe = new Subject<void>();

  public action!: string;
  public survey!: Survey;
  public surveyForm: UntypedFormGroup;

  constructor(
    private fb: UntypedFormBuilder,
    protected ref: NbDialogRef<EditSurveyComponent>,
    private store: Store,
    private actions$: Actions,
    private dialogService: NbDialogService,
    private toastrService: NbToastrService
  ) {
    this.surveyForm = this.fb.group({
      survey_id: ['', [Validators.required, Validators.pattern(/^[a-zA-Z0-9:_-]*$/i)]],
      survey_name: ['', Validators.required],
      survey_type: 'survey',
      language: 'en',
      locale: 'en-US',
      questions: this.fb.array([])
    });
  }

  public ngOnInit(): void {
    if (this.action === ACTIONS.EDIT) {
      this.surveyForm.controls.survey_id.disable();
    }

    this.actions$.pipe(ofActionCompleted(SaveSurveysComplete), takeUntil(this.unsubscribe)).subscribe((data) => {
      if (data) {
        this.ref.close();
      }
    });
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public ngAfterViewInit(): void {
    if (this.action === ACTIONS.EDIT || this.action === ACTIONS.CLONE) {
      // flush questions
      this.clearFormArray();
      // populate the form with empty fields so they can be patched with a value later
      this.survey.questions.forEach((q) => {
        const question: UntypedFormGroup = this.newQuestion();
        this.questions().push(question);

        q.answers.forEach((a) => {
          const answer = this.newAnswer();

          (question.get('answers') as UntypedFormArray).push(answer);

          a.actions.forEach(() => {
            (answer.get('actions') as UntypedFormArray).push(this.newAction());
          });
        });
      });
      // patch values of the form with survey data
      this.surveyForm.patchValue(this.survey);
    }
  }

  public clearFormArray() {
    this.questions().clear();
  }

  /* Questions */

  public questions(): FormArray<FormGroup> {
    return this.surveyForm.get('questions') as FormArray<FormGroup>;
  }

  public newQuestion(): UntypedFormGroup {
    return this.fb.group({
      question_id: ['', [Validators.required, Validators.pattern(/^[a-zA-Z0-9:_-]*$/i)]],
      question_type: ['', [Validators.required]],
      question_name: '',
      language: 'en',
      locale: 'en-US',
      visible: true,
      value_id: ' ',
      value: '',
      image_id: '',
      title_id: '',
      subtitle_id: '',
      display_order: null,
      answers: this.fb.array([])
    });
  }

  public addQuestion() {
    this.questions().push(this.newQuestion());
  }

  public removeQuestion(qi: number) {
    this.questions().removeAt(qi);
  }

  /* Answers */

  public answers(qi: number): UntypedFormArray {
    return this.questions().at(qi).get('answers') as UntypedFormArray;
  }

  public newAnswer(): UntypedFormGroup {
    return this.fb.group({
      answer_id: ['', [Validators.required, Validators.pattern(/^[a-zA-Z0-9:_-]*$/i)]],
      answer_type: '',
      value: '',
      value_id: '',
      min_value: null,
      min_value_text: '',
      min_value_text_id: '',
      max_value: null,
      max_value_text: '',
      max_value_text_id: '',
      language: 'en',
      locale: 'en-US',
      image_id: '',
      actions: this.fb.array([])
    });
  }

  public addAnswer(qi: number) {
    this.answers(qi).push(this.newAnswer());
  }

  public removeAnswer(qi: number, ai: number) {
    this.answers(qi).removeAt(ai);
  }

  /* Actions */

  public actions(qi: number, ai: number): UntypedFormArray {
    return this.answers(qi).at(ai).get('actions') as UntypedFormArray;
  }

  public newAction(): UntypedFormGroup {
    const ap = this.fb.group({ url: '' }) as UntypedFormGroup;

    return this.fb.group({
      action_type: ['', [Validators.required]],
      action_value: '',
      action_payload: ap
    });
  }

  public addAction(qi: number, ai: number) {
    this.actions(qi, ai).push(this.newAction());
  }

  public removeAction(qi: number, ai: number, aci: number) {
    this.actions(qi, ai).removeAt(aci);
  }

  public save() {
    let edit;
    if (this.action === ACTIONS.EDIT) {
      edit = true;
    }
    this.store.dispatch(new PostSurveys(this.surveyForm.getRawValue(), edit));
  }

  public onSubmit() {
    if (this.action === ACTIONS.CREATE || this.action === ACTIONS.CLONE) {
      if (this.uniqueSurveyName() && this.uniqueSurveyId()) {
        this.save();
      } else if (!this.uniqueSurveyName()) {
        this.toastrService.show(status, $localize`Survey name has to be unique!`, { status: 'danger' });
      } else if (!this.uniqueSurveyId()) {
        this.toastrService.show(status, $localize`Survey ID has to be unique!`, { status: 'danger' });
      }
    }
    if (this.action === ACTIONS.EDIT) {
      if (this.uniqueSurveyName()) {
        this.save();
      } else {
        this.toastrService.show(status, $localize`Survey name has to be unique!`, { status: 'danger' });
      }
    }
  }

  public uniqueSurveyName() {
    const surveys = this.store.selectSnapshot(SurveysState.surveys) || [];
    const surveyName = this.surveyForm.getRawValue().survey_name;
    const surveyId = this.surveyForm.getRawValue().survey_id;

    if (this.action === ACTIONS.CREATE || this.action === ACTIONS.CLONE) {
      return !surveys.some((survey) => surveyName === survey.survey_name);
    } else if (this.action === ACTIONS.EDIT) {
      return !surveys.some((survey) => surveyName === survey.survey_name && surveyId !== survey.survey_id);
    } else {
      return false;
    }
  }

  public uniqueSurveyId() {
    const surveys = this.store.selectSnapshot(SurveysState.surveys) || [];
    const surveyId = this.surveyForm.getRawValue().survey_id;

    return !surveys.some((survey) => survey.survey_id === surveyId);
  }

  public close() {
    this.dialogService
      .open(NgxGenericModalComponent, {
        context: {
          title: $localize`Confirm Close`,
          message: $localize`Are you sure that you want to close the window? Any changes that you have made will be lost.`
        }
      })
      .onClose.subscribe((data) => {
        if (data) {
          this.ref.close();
        }
      });
  }
}
